/**
 * Runtime feature flags.
 *
 * Flags are read from Vite env vars (`VITE_FEATURE_*`) and default to OFF.
 * A flag is enabled only when its env var is exactly the string `"true"`,
 * so anything unset/misspelled stays disabled.
 *
 * Flags are exposed as functions (rather than precomputed constants) so the
 * env can be stubbed per-test with `vi.stubEnv`.
 */
const isEnabled = (value: string | undefined): boolean => value === 'true';

export const featureFlags = {
  /**
   * Inline comments on individual test items in the report editor.
   * Disabled by default.
   */
  testItemComments: (): boolean =>
    isEnabled(import.meta.env.VITE_FEATURE_TEST_ITEM_COMMENTS),

  /**
   * Billing and subscription surface: the Organisation > Billing tab
   * (payment methods, subscription status, invoices, license management) and
   * the purchase/upgrade flows that route into it.
   *
   * Disabled by default. Trial-then-lock access gating stays active regardless
   * of this flag; when disabled, upgrade CTAs fall back to "contact us".
   */
  billingEnabled: (): boolean =>
    isEnabled(import.meta.env.VITE_FEATURE_BILLING),

  /**
   * Test Guidance (internal-facing rich-text help on a Test Specification) and
   * Reference Images. Disabled by default; gates the authoring UI on web.
   */
  testGuidance: (): boolean =>
    isEnabled(import.meta.env.VITE_FEATURE_TEST_GUIDANCE),
} as const;
